import { UserPlus, UserSquare2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { NewCustomerDialog } from '../accounts/NewCustomerDialog';
import { paths } from '../accounts/shared';
import { RegisterDomainButton } from '../operator/RegisterDomain';
import { TransferInButton } from '../operator/TransferIn';
import { SubjectFormDialog } from '../subjects/SubjectForm';

export type QuickAction = 'register' | 'transfer-in' | 'customer' | 'subject';

/**
 * Home hub quick actions: register a domain for a customer, transfer one in, create a customer,
 * create a subject. `?action=register|transfer-in|customer|subject` opens one of them (used by the
 * Ctrl-K menu; `&domain=` prefills the transfer-in); closing the dialog removes the parameter.
 */
export function HomeQuickActions() {
  const { t } = useTranslation('nav');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const requested = params.get('action') as QuickAction | null;
  const [chosen, setChosen] = useState<QuickAction | null>(null);
  const open = requested ?? chosen;
  const setOpen = (next: QuickAction | null) => {
    setChosen(next);
    if (!next && requested) {
      const p = new URLSearchParams(params);
      p.delete('action');
      p.delete('domain');
      setParams(p, { replace: true });
    }
  };
  const close = (o: boolean) => (o ? undefined : setOpen(null));
  return (
    <>
      <RegisterDomainButton open={open === 'register'} onOpenChange={(o) => setOpen(o ? 'register' : null)} />
      <TransferInButton open={open === 'transfer-in'} onOpenChange={(o) => setOpen(o ? 'transfer-in' : null)} domain={params.get('domain') ?? ''} />
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen('customer')}>
        <UserPlus /> {t('ui.newCustomer')}
      </Button>
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen('subject')}>
        <UserSquare2 /> {t('ui.newSubject')}
      </Button>
      <NewCustomerDialog open={open === 'customer'} onOpenChange={close} onCreated={(id) => void navigate(paths.customer(id))} />
      <SubjectFormDialog open={open === 'subject'} onOpenChange={close} onSaved={(id) => void navigate(paths.subject(id))} />
    </>
  );
}
